import {
	CurrencyDollarIcon,
	CashIcon,
	LibraryIcon,
	ClockIcon,
} from "@heroicons/react/outline";
import { useSingleItem } from "../../hooks/useFetch";
import { NavLink, Route, Switch, useLocation } from "react-router-dom";
import Category from "../../shared/Category";
import ProductImages from "../../shared/ImageSet/ProductImages";
import { IProduct } from "../../types";
import toRupiah from "../../utils/toRupiah";
import { formatDate2 } from "../../utils/wordUtils";
import DetailTable from "admin/src/components/Table/Product/DetailTable";
import ProductStatus from "./ProductStatus";
import ProductUsers from "./ProductUsers";
import ProductLogsTable from "admin/src/components/Table/Product/ProductLogsTable";

type NavLinkType = {
	name: string;
	description: string;
};

type MainType = {
	name: string;
	icon: React.ReactNode;
};

const navLinks: NavLinkType[] = [
	{ name: "Info", description: "Detailed information" },
	{ name: "Status", description: "User status" },
	{ name: "Viewer", description: "Who see this thing" },
	{ name: "Logs", description: "Timeline information" },
];

const ProductDetail = () => {
	const { pathname } = useLocation();
	const [product, loading] = useSingleItem<IProduct>(
		pathname.split("/")[3],
		"product"
	);

	const main: MainType[] = [
		{
			name: `Harga: Rp. ${toRupiah(product.price || 0)}`,
			icon: <CurrencyDollarIcon className="h-4 w-4" />,
		},
		{
			name: `Deposit: ${toRupiah(product.deposit || 0)}`,
			icon: <CashIcon className="h-4 w-4" />,
		},
		{
			name: `Minimal Rent: ${toRupiah(product.minRent || 1)} day`,
			icon: <LibraryIcon className="h-4 w-4" />,
		},
		{
			name: `Since ${formatDate2(product.createdAt || "")}`,
			icon: <ClockIcon className="h-4 w-4" />,
		},
	];
	return (
		<div className="mt-10 shadow-lg py-10 px-10 bg-white rounded-xl w-full h-full">
			<div id="head" className="flex gapx-10 h-full  ">
				<div
					id="user-avatar"
					className={`relative bg-white w-80 h-80 rounded-lg mr-10 flex-shrink-0 inline-flex items-center justify-center text-neutral-100 uppercase font-semibold shadow-inner ${
						loading && "animate-pulse bg-gray-300"
					}`}
				>
					{loading || <ProductImages images={product.images || []} />}
					<span
						className={` bg-sky-400 h-10 w-10 rounded-full text-white text-xs flex items-center justify-center absolute -top-4 -right-4 shadow-md border-4 border-white z-20`}
					>
						<i className="fas fa-check "></i>
					</span>
				</div>

				<div className="flex flex-col align-middle justify-center text-slate-600">
					<Category className="mb-2 text-sm" category={product.category} />
					<h2 className="font-bold first-letter:uppercase text-4xl">
						{product.name || ""}
					</h2>
					<p className="text-opacity-80">{product.owner?.displayName}</p>
					<div id="property" className="grid gap-y-2 mt-4">
						{main.map((v) => {
							return (
								<div
									key={v.name}
									className="flex items-center gap-x-3 text-slate-500 text-xs"
								>
									{" "}
									{v.icon}
									<p>{v.name}</p>
								</div>
							);
						})}
					</div>
				</div>
			</div>
			<div className="bg-white overflow-hidden sm:rounded-lg w-full h-full">
				<nav className="my-6">
					<ul className="grid grid-cols-4">
						{navLinks.map((li) => {
							return (
								<NavLink
									to={`${li.name.toLowerCase()}`}
									className="px-4 py-5 sm:px-6 opacity-50 hover:bg-sky-50"
									activeClassName="border-b-2  border-sky-500 px-4 py-5 sm:px-6"
									key={li.name}
								>
									<h3 className="text-lg leading-6 font-medium text-gray-900">
										{li.name}
									</h3>
									<p className="mt-1 max-w-2xl text-sm text-gray-500">
										{li.description}
									</p>
								</NavLink>
							);
						})}
					</ul>
				</nav>
				<Switch>
					<Route
						path="/dashboard/product/:id/info"
						children={<DetailTable context={product} />}
					/>
					<Route
						path="/dashboard/product/:id/status"
						children={<ProductStatus context={product} />}
					/>
					<Route
						path="/dashboard/product/:id/viewer"
						children={<ProductUsers context={product} />}
					/>
					<Route
						path="/dashboard/product/:id/logs"
						children={<ProductLogsTable context={product} />}
					/>
				</Switch>
				{/* <Outlet context={product} /> */}
			</div>
		</div>
	);
};

export default ProductDetail;
